import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, getAuth, clearAuth } from "@/lib/api";
import { useOpenDestination } from "@/lib/signIn";
import { LogOut, MessageCircle, ShieldCheck, User as UserIcon, CalendarDays, FileText, Lock, ChevronRight } from "lucide-react";

function Row({ to, icon: Icon, title, subtitle, testid }) {
  return (
    <Link to={to} className="brand-card p-4 flex items-center gap-3 min-h-[60px]" data-testid={testid}>
      <Icon className="h-5 w-5 brand-primary-text flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm">{title}</div>
        {subtitle && <div className="text-xs brand-text-muted">{subtitle}</div>}
      </div>
      <ChevronRight className="h-4 w-4 brand-text-muted flex-shrink-0" />
    </Link>
  );
}

export default function TenantAccount() {
  const { slug } = useParams();
  const nav = useNavigate();
  const user = getAuth()?.user;
  const base = `/t/${slug}`;
  const open = useOpenDestination();
  const [joining, setJoining] = useState(false);

  const logout = () => { clearAuth(); nav(base); };

  // Someone already signed in to this business's admin console or seller portal
  // (or the Super Admin) with no customer account here yet. Their number is
  // verified, so they can also use the customer app with it, without a new code.
  const staff = !user && [[`t:${slug}:admin`, "the business admin"], [`t:${slug}:seller`, "a seller"], ["super", "the Super Admin"]]
    .map(([scope, label]) => ({ session: getAuth(scope), label })).find((x) => x.session);
  const continueAsCustomer = async () => {
    setJoining(true);
    try {
      const r = await api.post("/auth/continue-as-customer", { tenant_slug: slug }, { headers: { Authorization: `Bearer ${staff.session.token}` } });
      open(r.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "That did not work. Please sign in.");
      setJoining(false);
    }
  };

  return (
    <div className="pwa-narrow px-5 py-5 md:py-8" data-testid="tenant-account-page">
      <h1 className="brand-heading font-bold text-2xl md:text-3xl">Account</h1>

      {!user ? (
        <div className="brand-card p-6 mt-5 text-center">
          <UserIcon className="h-10 w-10 brand-primary-text mx-auto" />
          <div className="font-bold mt-3">Sign in to continue</div>
          <div className="text-xs brand-text-muted mt-1">Track your bookings and manage your account</div>
          <Link to={`${base}/login?next=${base}/account`} className="block mt-4 brand-primary py-3 rounded-full font-semibold text-sm" data-testid="tenant-account-login-btn">Sign in</Link>
          {staff && (
            <div className="mt-4 pt-4 border-t brand-border text-left" data-testid="tenant-account-staff">
              <div className="text-xs brand-text-muted break-anywhere">You are signed in as {staff.label} ({staff.session.user.phone}).</div>
              <button type="button" onClick={continueAsCustomer} disabled={joining} className="mt-2 w-full brand-muted brand-text py-3 rounded-full font-semibold text-sm disabled:opacity-50 min-h-[44px]" data-testid="tenant-account-continue-as-customer">
                {joining ? "Opening..." : "Use the customer app with this number"}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="brand-card p-5 mt-5" data-testid="tenant-account-profile">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 brand-primary rounded-full flex items-center justify-center font-bold flex-shrink-0">
              {(user.name || user.phone || "U")[0]}
            </div>
            <div className="min-w-0">
              <div className="font-bold break-words">{user.name || "User"}</div>
              <div className="text-xs brand-text-muted break-anywhere">{user.phone || user.email}</div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-5 space-y-2">
        {user && <Row to={`${base}/bookings`} icon={CalendarDays} title="My bookings" subtitle="Upcoming and past bookings" testid="tenant-account-bookings-link" />}
        {user && <Row to={`${base}/account/security`} icon={Lock} title="Security" subtitle="Delete your account" testid="tenant-account-security-link" />}
        <Row to={`${base}/enquiry`} icon={MessageCircle} title="Send enquiry" subtitle="Ask us anything" testid="tenant-account-enquiry-link" />
      </div>

      <div className="mt-6 text-xs uppercase tracking-wider font-semibold brand-text-muted">Legal</div>
      <div className="mt-2 space-y-2">
        <Row to={`${base}/privacy-policy`} icon={FileText} title="Privacy Policy" testid="tenant-account-privacy-link" />
        <Row to={`${base}/terms`} icon={FileText} title="Terms & Conditions" testid="tenant-account-terms-link" />
        <Row to={`${base}/account-deletion`} icon={FileText} title="Account Deletion" subtitle="How to delete your account, and what is kept" testid="tenant-account-deletion-link" />
      </div>

      <div className="mt-6 text-xs uppercase tracking-wider font-semibold brand-text-muted">For the business</div>
      <div className="mt-2 space-y-2">
        <Row to={`${base}/admin`} icon={ShieldCheck} title="Business admin" subtitle="For the business owner" testid="tenant-account-admin-link" />
        <Row to={`${base}/seller`} icon={ShieldCheck} title="Seller / Vendor" subtitle="For service providers" testid="tenant-account-seller-link" />
        {getAuth("super") && <Row to="/super" icon={ShieldCheck} title="Super Admin" subtitle="Manage the platform" testid="tenant-account-super-link" />}
      </div>

      {user && (
        <button onClick={logout} className="mt-6 w-full brand-muted brand-text py-3 rounded-full font-semibold text-sm flex items-center justify-center gap-2" data-testid="tenant-account-logout">
          <LogOut className="h-4 w-4" /> Logout
        </button>
      )}
    </div>
  );
}
